import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { bankAccountsAPI, expensesAPI, incomesAPI, investmentsAPI, bankDataAPI, quipuAPI, payrollAPI, financeAPI, recurringExpensesAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';
import BillingIncidentsSection from './BillingIncidentsSection';
import { ProfitabilityTab } from './ProfitabilityTab';
import { ExpenseForecastSection } from './ExpenseForecastSection';
import { ExpenseInvoicesCard } from './ExpenseInvoicesCard';
import { BankSyncAlert } from './BankSyncAlert';
import { BankConsentCard, FinanceWebhookCard } from './FinanceAlertSettings';
import { FinancePeriodSelector, defaultFinancePeriod, resolveFinancePeriod, fmtDateEs } from './FinancePeriodSelector';
import {
  Building,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  PiggyBank,
  Calculator,
  Car,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  Plus,
  Wrench,
  Users,
  Landmark,
  RefreshCw,
  ExternalLink,
  Download,
  Filter,
  AlertTriangle,
  BarChart3,
  Loader2,
  Search,
  X,
  Tag,
  Save,
  ChevronDown,
  Edit2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#f56258', '#ec4899', '#06b6d4', '#84cc16'];

// Default (built-in) transaction categories
const DEFAULT_CATEGORIES = [
  { name: 'Cobro cliente',       bg: '#EAF3DE', text: '#3B6D11', type: 'Ingreso' },
  { name: 'Comision bancaria',   bg: '#FCEBEB', text: '#A32D2D', type: 'Gasto' },
  { name: 'Herramienta digital', bg: '#E6F1FB', text: '#185FA5', type: 'Gasto' },
  { name: 'Emergent',            bg: '#EEEDFE', text: '#534AB7', type: 'Gasto' },
  { name: 'Proveedores',         bg: '#FAEEDA', text: '#854F0B', type: 'Gasto' },
  { name: 'Nominas equipo',      bg: '#E1F5EE', text: '#0F6E56', type: 'Gasto' },
  { name: 'Nominas CEOs',        bg: '#085041', text: '#9FE1CB', type: 'Gasto' },
  { name: 'Asesoria',            bg: '#FBEAF0', text: '#993556', type: 'Gasto' },
  { name: 'Viajes',              bg: '#F1EFE8', text: '#5F5E5A', type: 'Gasto' },
  { name: 'Dietas',              bg: '#FAECE7', text: '#993C1D', type: 'Gasto' },
  { name: 'Formacion',           bg: '#F4C0D1', text: '#72243E', type: 'Gasto' },
];

// Auto-generate contrasting text color from bg
const getContrastText = (bgHex) => {
  try {
    const r = parseInt(bgHex.slice(1, 3), 16);
    const g = parseInt(bgHex.slice(3, 5), 16);
    const b = parseInt(bgHex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? '#333333' : '#FFFFFF';
  } catch (e) { return '#333333'; }
};

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Helper to get a tx key for tagging (NO dots - MongoDB uses dots as path separators)
const getTxKey = (tx) => {
  const dateStr = (tx.date_iso || tx.date || '').replace(/\./g, '_');
  const desc = (tx.description || '').substring(0, 50).replace(/[^a-zA-Z0-9]/g, '_');
  const amount = (tx.amount || 0).toFixed(2).replace(/\./g, '_');
  return `${dateStr}_${desc}_${amount}`;
};

export function FinancePage() {
  const { user } = useAuthStore();
  const isCEO = user?.role === 'ceo';
  const [activeTab, setActiveTab] = useState('banco-real');
  const [loading, setLoading] = useState(false);

  // Bank data
  const [realBankAccounts, setRealBankAccounts] = useState([]);
  const [realTransactions, setRealTransactions] = useState([]);
  const [bankSummary, setBankSummary] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState(null);

  // Transaction filters
  const [txSortOrder, setTxSortOrder] = useState('fecha-desc');
  const [txDateFrom, setTxDateFrom] = useState('');
  const [txDateTo, setTxDateTo] = useState('');
  const [txAmountMin, setTxAmountMin] = useState('');
  const [txAmountMax, setTxAmountMax] = useState('');
  const [txSearchQuery, setTxSearchQuery] = useState('');
  const [txSearchInput, setTxSearchInput] = useState('');
  const [txCategoryFilter, setTxCategoryFilter] = useState('');

  // Quipu data
  const [quipuData, setQuipuData] = useState(null);
  const [quipuLoading, setQuipuLoading] = useState(false);
  const [quipuError, setQuipuError] = useState(null);
  const [quipuConnected, setQuipuConnected] = useState(false);

  // Month selector for Quipu
  const [selectedQuipuMonth, setSelectedQuipuMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Manual payroll
  const [payrollData, setPayrollData] = useState({});
  const [payrollInput, setPayrollInput] = useState('');
  const [payrollSaving, setPayrollSaving] = useState(false);

  // Transaction categories/tags
  const [transactionTags, setTransactionTags] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [selectedTxForModal, setSelectedTxForModal] = useState(null);
  const [catModalView, setCatModalView] = useState('list'); // 'list' | 'new' | 'edit'
  const [catFormData, setCatFormData] = useState({ name: '', bg: '#E6F1FB', type: 'Gasto' });
  const [editingCatIdx, setEditingCatIdx] = useState(null);

  // Período global de Finanzas (KPIs, Banco Real, Gastos, Ingresos)
  const [finPeriod, setFinPeriod] = useState(defaultFinancePeriod);

  // Conciliation period
  const [concPeriodFrom, setConcPeriodFrom] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [concPeriodTo, setConcPeriodTo] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  // Sync state
  const [lastBankUpdate, setLastBankUpdate] = useState(null);
  const [lastQuipuUpdate, setLastQuipuUpdate] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Investments
  const [investments, setInvestments] = useState([]);

  // --- Data loading ---

  const loadCachedData = useCallback(() => {
    try {
      const cachedBank = localStorage.getItem('finance_bank_cache');
      const cachedQuipu = localStorage.getItem('finance_quipu_cache');
      const cachedTransactions = localStorage.getItem('finance_transactions_cache');

      if (cachedBank) {
        const data = JSON.parse(cachedBank);
        setRealBankAccounts(data.accounts || []);
        setBankSummary(data.summary || null);
        setLastBankUpdate(data.timestamp);
      }
      if (cachedTransactions) {
        const data = JSON.parse(cachedTransactions);
        setRealTransactions(data.transactions || []);
      }
      if (cachedQuipu) {
        const data = JSON.parse(cachedQuipu);
        setQuipuData(data.data);
        setQuipuConnected(data.connected);
        setLastQuipuUpdate(data.timestamp);
      }
    } catch (e) {
      console.error('Error loading cache:', e);
    }
  }, []);

  const saveBankCache = (accounts, summary, transactions) => {
    try {
      const timestamp = new Date().toISOString();
      localStorage.setItem('finance_bank_cache', JSON.stringify({ accounts, summary, timestamp }));
      localStorage.setItem('finance_transactions_cache', JSON.stringify({ transactions, timestamp }));
      setLastBankUpdate(timestamp);
    } catch (e) { /* ignore */ }
  };

  const saveQuipuCache = (data, connected) => {
    try {
      const timestamp = new Date().toISOString();
      localStorage.setItem('finance_quipu_cache', JSON.stringify({ data, connected, timestamp }));
      setLastQuipuUpdate(timestamp);
    } catch (e) { /* ignore */ }
  };

  const loadBankDataBackground = async () => {
    setBankLoading(true);
    setBankError(null);
    try {
      const [accountsData, transactionsData, summaryData] = await Promise.all([
        bankDataAPI.getAccounts(),
        bankDataAPI.getTransactions(5000),
        bankDataAPI.getSummary(),
      ]);
      const accounts = accountsData?.accounts || [];
      const transactions = transactionsData?.transactions || [];
      const summary = summaryData || null;
      setRealBankAccounts(accounts);
      setRealTransactions(transactions);
      setBankSummary(summary);
      saveBankCache(accounts, summary, transactions);
    } catch (error) {
      console.error('Error loading bank data:', error);
      setBankError('Error cargando datos bancarios');
    } finally {
      setBankLoading(false);
    }
  };

  const loadQuipuDataBackground = async () => {
    setQuipuLoading(true);
    setQuipuError(null);
    try {
      const status = await quipuAPI.getStatus();
      setQuipuConnected(status.connected);
      if (status.connected) {
        const financeData = await quipuAPI.getFinanceSummary();
        setQuipuData(financeData);
        saveQuipuCache(financeData, true);
      } else {
        saveQuipuCache(null, false);
      }
    } catch (error) {
      console.error('Error loading Quipu:', error);
      setQuipuError('Error cargando Quipu');
    } finally {
      setQuipuLoading(false);
    }
  };

  const loadTransactionTags = async () => {
    try {
      const data = await bankDataAPI.getTransactionTags();
      setTransactionTags(data.tags || {});
      if (data.categories && data.categories.length > 0) {
        setCustomCategories(data.categories);
      }
    } catch (e) {
      console.error('Error loading transaction tags:', e);
    }
  };

  const loadPayrollData = async () => {
    try {
      const data = await payrollAPI.getAll();
      const map = {};
      (data.entries || []).forEach(e => { map[e.year_month] = e.amount; });
      setPayrollData(map);
    } catch (e) {
      console.error('Error loading payroll:', e);
    }
  };

  const loadInvestments = async () => {
    try {
      const data = await investmentsAPI.getAll();
      setInvestments(data || []);
    } catch (e) {
      console.error('Error loading investments:', e);
    }
  };

  const syncAllInBackground = async () => {
    setIsSyncing(true);
    await Promise.allSettled([
      loadBankDataBackground(),
      loadQuipuDataBackground(),
      loadTransactionTags(),
      loadPayrollData(),
      loadInvestments(),
    ]);
    setIsSyncing(false);
  };

  useEffect(() => {
    loadCachedData();
    syncAllInBackground();
  }, []);

  // Update payroll input when month changes
  useEffect(() => {
    setPayrollInput(payrollData[selectedQuipuMonth]?.toString() || '');
  }, [selectedQuipuMonth, payrollData]);

  // --- Handlers ---

  const handleSync = async () => {
    if (isSyncing) return;
    await syncAllInBackground();
  };

  const handleSavePayroll = async () => {
    setPayrollSaving(true);
    try {
      const amount = parseFloat(payrollInput) || 0;
      await payrollAPI.save(selectedQuipuMonth, amount);
      setPayrollData(prev => ({ ...prev, [selectedQuipuMonth]: amount }));
    } catch (e) {
      console.error('Error saving payroll:', e);
    } finally {
      setPayrollSaving(false);
    }
  };

  const handleTagTransaction = async (txKey, category) => {
    try {
      await bankDataAPI.saveTransactionTag(txKey, category);
      setTransactionTags(prev => {
        const updated = { ...prev };
        if (category) {
          updated[txKey] = category;
        } else {
          delete updated[txKey];
        }
        return updated;
      });
      setSelectedTxForModal(null);
      setCatModalView('list');
    } catch (e) {
      console.error('Error saving tag:', e);
    }
  };

  // ===== Gastos recurrentes (marca en el modal) =====
  const [recurringRules, setRecurringRules] = useState([]);
  const [forecastRefresh, setForecastRefresh] = useState(0);

  useEffect(() => {
    recurringExpensesAPI.list().then((d) => setRecurringRules(d.rules || [])).catch(() => {});
  }, []);

  const handleSetRecurrence = async (tx, periodicity) => {
    const txKey = getTxKey(tx);
    const existing = recurringRules.find((r) => r.source_tx_key === txKey);
    try {
      if (periodicity === 'puntual') {
        if (existing) {
          await recurringExpensesAPI.remove(existing.id);
          setRecurringRules((prev) => prev.filter((r) => r.id !== existing.id));
          toast.success('Marcado como gasto puntual');
        }
      } else if (existing) {
        await recurringExpensesAPI.update(existing.id, { periodicity });
        setRecurringRules((prev) => prev.map((r) => (r.id === existing.id ? { ...r, periodicity } : r)));
        toast.success(`Recurrencia: ${periodicity}`);
      } else {
        const day = parseInt(String(tx.date_iso || '').slice(8, 10), 10) || 1;
        const rule = await recurringExpensesAPI.create({
          label: (tx.description || 'Gasto').slice(0, 60),
          category: getTxDisplayCategory(tx) || null,
          amount: Math.abs(tx.amount || 0),
          periodicity,
          charge_day: day,
          source_tx_key: txKey,
          description: tx.description || '',
        });
        setRecurringRules((prev) => [...prev, rule]);
        toast.success(`Gasto recurrente ${periodicity} creado — lo verás en la previsión (pestaña Gastos)`);
      }
      setForecastRefresh((t) => t + 1);
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar la recurrencia');
    }
  };

  const saveCustomCategoriesToBackend = async (cats) => {
    try {
      await bankDataAPI.saveTransactionCategories(cats);
    } catch (e) {
      console.error('Error saving categories:', e);
    }
  };

  const handleSaveNewCategory = async () => {
    const name = catFormData.name.trim();
    if (!name) return;
    const newCat = { name, bg: catFormData.bg, text: getContrastText(catFormData.bg), type: catFormData.type };
    const updated = [...customCategories, newCat];
    setCustomCategories(updated);
    await saveCustomCategoriesToBackend(updated);
    setCatFormData({ name: '', bg: '#E6F1FB', type: 'Gasto' });
    setCatModalView('list');
  };

  const handleUpdateCategory = async () => {
    if (editingCatIdx === null) return;
    const name = catFormData.name.trim();
    if (!name) return;
    const oldName = customCategories[editingCatIdx]?.name;
    const updatedCat = { name, bg: catFormData.bg, text: getContrastText(catFormData.bg), type: catFormData.type };
    const updated = customCategories.map((c, i) => i === editingCatIdx ? updatedCat : c);
    setCustomCategories(updated);
    await saveCustomCategoriesToBackend(updated);
    // If name changed, update all transaction tags that used the old name
    if (oldName && oldName !== name) {
      const newTags = { ...transactionTags };
      let changed = false;
      Object.keys(newTags).forEach(k => {
        if (newTags[k] === oldName) { newTags[k] = name; changed = true; }
      });
      if (changed) {
        setTransactionTags(newTags);
        for (const [k, v] of Object.entries(newTags)) {
          if (v === name) await bankDataAPI.saveTransactionTag(k, name);
        }
      }
    }
    setEditingCatIdx(null);
    setCatFormData({ name: '', bg: '#E6F1FB', type: 'Gasto' });
    setCatModalView('list');
  };

  const handleDeleteCategory = async (idx) => {
    const catName = customCategories[idx]?.name;
    const updated = customCategories.filter((_, i) => i !== idx);
    setCustomCategories(updated);
    await saveCustomCategoriesToBackend(updated);
    // Remove tags that used this category
    if (catName) {
      const newTags = { ...transactionTags };
      Object.keys(newTags).forEach(k => {
        if (newTags[k] === catName) {
          delete newTags[k];
          bankDataAPI.saveTransactionTag(k, '');
        }
      });
      setTransactionTags(newTags);
    }
  };

  const startEditCategory = (idx) => {
    const cat = customCategories[idx];
    setCatFormData({ name: cat.name, bg: cat.bg, type: cat.type });
    setEditingCatIdx(idx);
    setCatModalView('edit');
  };

  // --- Computed values ---

  // All categories = defaults + custom
  const allCategories = React.useMemo(
    () => [...DEFAULT_CATEGORIES, ...customCategories],
    [customCategories]
  );

  // Quipu month data
  // Gastos SIEMPRE desde el banco (Fintable), nunca desde facturas de gasto de Quipu
  const bankExpensesByMonth = React.useMemo(() => {
    const out = {};
    realTransactions.forEach((tx) => {
      const m = (tx.date_iso || tx.date || '').slice(0, 7);
      if (m.length !== 7) return;
      if (!out[m]) out[m] = { expenses: 0, income: 0, count: 0 };
      out[m].count += 1;
      if (tx.amount < 0) out[m].expenses += Math.abs(tx.amount); else out[m].income += tx.amount;
    });
    return out;
  }, [realTransactions]);
  const bankYtd = React.useMemo(() => {
    const y = String(new Date().getFullYear());
    return Object.entries(bankExpensesByMonth).filter(([m]) => m.startsWith(y)).reduce((acc, [, v]) => ({ expenses: acc.expenses + v.expenses, income: acc.income + v.income }), { expenses: 0, income: 0 });
  }, [bankExpensesByMonth]);
  const quipuChartData = React.useMemo(() => (quipuData?.monthly_chart || []).map((row) => ({
    month: row.month, income: row.income, expenses: Math.round((bankExpensesByMonth[row.month]?.expenses || 0) * 100) / 100,
  })), [quipuData, bankExpensesByMonth]);

  const quipuMonthData = React.useMemo(() => {
    if (!quipuData) return { income: 0, expenses: 0, paysheets: 0, net: 0, bankHasData: false };
    const incomeByMonth = quipuData.income_by_month || {};
    const income = incomeByMonth[selectedQuipuMonth] || 0;
    const bankMonth = bankExpensesByMonth[selectedQuipuMonth];
    const expenses = bankMonth?.expenses || 0;
    const bankHasData = !!bankMonth;
    const manualPayroll = payrollData[selectedQuipuMonth] || 0;
    const paysheets = selectedQuipuMonth === quipuData.this_month?.paysheets_month
      ? (quipuData.this_month?.paysheets || 0)
      : 0;
    const net = income - expenses - manualPayroll;
    return { income, expenses, paysheets, manualPayroll, net, bankHasData };
  }, [quipuData, selectedQuipuMonth, payrollData, bankExpensesByMonth]);

  // Available months for Quipu selector
  const availableQuipuMonths = React.useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const keys = new Set();
    for (let m = 1; m <= currentMonth; m++) keys.add(`${currentYear}-${String(m).padStart(2, '0')}`);
    Object.keys(quipuData?.income_by_month || {}).forEach((k) => { if ((quipuData.income_by_month[k] || 0) > 0) keys.add(k); });
    return Array.from(keys).sort().reverse().map((key) => ({ key, label: `${MONTH_NAMES[parseInt(key.slice(5, 7), 10) - 1]} ${key.slice(0, 4)}` }));
  }, [quipuData]);

  // Real bank data totals
  const realTotalBalance = bankSummary?.balance || realBankAccounts.reduce((sum, a) => sum + (a.balance || 0), 0);

  // Período seleccionado → movimientos reales del banco dentro del período (misma base para KPIs, Gastos e Ingresos)
  const { from: periodFrom, to: periodTo, label: periodLabel } = resolveFinancePeriod(finPeriod);
  const periodTransactions = React.useMemo(
    () => realTransactions.filter((tx) => { const d = tx.date_iso || tx.date || ''; return d >= periodFrom && d <= periodTo; }),
    [realTransactions, periodFrom, periodTo],
  );
  const realTotalIncome = periodTransactions.filter((tx) => tx.amount > 0).reduce((s, tx) => s + tx.amount, 0);
  const realTotalExpenses = periodTransactions.filter((tx) => tx.amount < 0).reduce((s, tx) => s + Math.abs(tx.amount), 0);
  // Cobertura real de datos: banco (Fintable) y Quipu (facturación de ingresos)
  const bankFirstDate = React.useMemo(() => realTransactions.reduce((min, tx) => { const d = tx.date_iso || tx.date || ''; return d && (!min || d < min) ? d : min; }, ''), [realTransactions]);
  const quipuMonthsWithData = React.useMemo(() => Object.keys(quipuData?.income_by_month || {}).filter((m) => (quipuData.income_by_month[m] || 0) > 0).sort(), [quipuData]);
  const monthsWithData = React.useMemo(() => {
    const set = new Set(quipuMonthsWithData);
    realTransactions.forEach((tx) => { const m = (tx.date_iso || tx.date || '').slice(0, 7); if (m.length === 7) set.add(m); });
    return set;
  }, [realTransactions, quipuMonthsWithData]);
  const periodYears = React.useMemo(() => {
    const ys = new Set([new Date().getFullYear()]);
    monthsWithData.forEach((m) => ys.add(parseInt(m.slice(0, 4), 10)));
    return Array.from(ys).sort((a, b) => b - a);
  }, [monthsWithData]);
  const periodCoverage = `Datos disponibles · Banco (Fintable): ${bankFirstDate ? `desde ${fmtDateEs(bankFirstDate)}` : 'sin datos'} · Quipu (facturación de ingresos): ${quipuMonthsWithData.length ? `desde ${MONTH_NAMES[parseInt(quipuMonthsWithData[0].slice(5, 7), 10) - 1].toLowerCase()} ${quipuMonthsWithData[0].slice(0, 4)}` : 'sin datos'}`;
  const periodHasBankData = periodTransactions.length > 0;
  const periodBeforeBank = !!bankFirstDate && periodTo < bankFirstDate;

  // Transaction filtering
  const clearTxFilters = () => {
    setTxSortOrder('fecha-desc');
    setTxDateFrom('');
    setTxDateTo('');
    setTxAmountMin('');
    setTxAmountMax('');
    setTxSearchQuery('');
    setTxSearchInput('');
    setTxCategoryFilter('');
  };

  const applySearch = () => setTxSearchQuery(txSearchInput);

  // Get the display category for a transaction (tag or raw)
  const getTxDisplayCategory = (tx) => {
    const key = getTxKey(tx);
    return transactionTags[key] || null;
  };

  // Get category object by name
  const getCategoryObj = (catName) => allCategories.find(c => c.name === catName) || null;

  // Get category bg color for charts
  const getCategoryBg = (catName) => {
    const cat = getCategoryObj(catName);
    return cat?.bg || '#e5e7eb';
  };

  const getCategoryText = (catName) => {
    const cat = getCategoryObj(catName);
    return cat?.text || '#6b7280';
  };

  const filteredAndSortedTransactions = React.useMemo(() => {
    let filtered = [...realTransactions];

    if (txSearchQuery.trim()) {
      const query = txSearchQuery.toLowerCase();
      filtered = filtered.filter(tx =>
        (tx.description || '').toLowerCase().includes(query) ||
        (getTxDisplayCategory(tx) || '').toLowerCase().includes(query)
      );
    }
    if (txDateFrom) {
      filtered = filtered.filter(tx => (tx.date_iso || tx.date || '') >= txDateFrom);
    }
    if (txDateTo) {
      filtered = filtered.filter(tx => (tx.date_iso || tx.date || '') <= txDateTo);
    }
    if (txAmountMin !== '') {
      const min = parseFloat(txAmountMin);
      if (!isNaN(min)) filtered = filtered.filter(tx => Math.abs(tx.amount || 0) >= min);
    }
    if (txAmountMax !== '') {
      const max = parseFloat(txAmountMax);
      if (!isNaN(max)) filtered = filtered.filter(tx => Math.abs(tx.amount || 0) <= max);
    }
    if (txCategoryFilter) {
      if (txCategoryFilter === '__uncategorized__') {
        filtered = filtered.filter(tx => !getTxDisplayCategory(tx));
      } else {
        filtered = filtered.filter(tx => getTxDisplayCategory(tx) === txCategoryFilter);
      }
    }

    filtered.sort((a, b) => {
      const dateA = a.date_iso || a.date || '';
      const dateB = b.date_iso || b.date || '';
      switch (txSortOrder) {
        case 'fecha-asc': return dateA.localeCompare(dateB);
        case 'fecha-desc': return dateB.localeCompare(dateA);
        case 'importe-desc': return Math.abs(b.amount || 0) - Math.abs(a.amount || 0);
        case 'importe-asc': return Math.abs(a.amount || 0) - Math.abs(b.amount || 0);
        default: return 0;
      }
    });
    return filtered;
  }, [realTransactions, txSortOrder, txDateFrom, txDateTo, txAmountMin, txAmountMax, txSearchQuery, txCategoryFilter, transactionTags]);

  // All unique categories used in transactions
  const allUsedCategories = React.useMemo(() => {
    const cats = new Set();
    realTransactions.forEach(tx => {
      const c = getTxDisplayCategory(tx);
      if (c) cats.add(c);
    });
    return Array.from(cats).sort();
  }, [realTransactions, transactionTags]);

  // Conciliation: filter transactions by selected period
  const concBankData = React.useMemo(() => {
    const filtered = realTransactions.filter(tx => {
      const d = tx.date_iso || tx.date || '';
      return d >= concPeriodFrom && d <= concPeriodTo;
    });
    const income = filtered.filter(tx => tx.amount > 0).reduce((s, tx) => s + tx.amount, 0);
    const expenses = filtered.filter(tx => tx.amount < 0).reduce((s, tx) => s + Math.abs(tx.amount), 0);
    return { income, expenses, count: filtered.length, net: income - expenses };
  }, [realTransactions, concPeriodFrom, concPeriodTo]);

  // Conciliation: Quipu data for the same period
  const concQuipuData = React.useMemo(() => {
    if (!quipuData) return { income: 0, expenses: 0, net: 0 };
    const incomeByMonth = quipuData.income_by_month || {};
    const expenseByMonth = quipuData.expense_by_month || {};

    const fromMonth = concPeriodFrom.substring(0, 7);
    const toMonth = concPeriodTo.substring(0, 7);

    let income = 0, expenses = 0;
    Object.keys(incomeByMonth).forEach(m => {
      if (m >= fromMonth && m <= toMonth) income += incomeByMonth[m];
    });
    Object.keys(expenseByMonth).forEach(m => {
      if (m >= fromMonth && m <= toMonth) expenses += expenseByMonth[m];
    });
    return { income, expenses, net: income - expenses };
  }, [quipuData, concPeriodFrom, concPeriodTo]);

  // Gastos: transactions tagged with categories of type 'Gasto'
  const expensesByCategory = React.useMemo(() => {
    const groups = {};
    periodTransactions.forEach(tx => {
      const catName = getTxDisplayCategory(tx);
      if (!catName) return;
      const catObj = getCategoryObj(catName);
      if (!catObj || catObj.type !== 'Gasto') return;
      if (!groups[catName]) groups[catName] = { category: catName, total: 0, count: 0, transactions: [] };
      groups[catName].total += Math.abs(tx.amount);
      groups[catName].count += 1;
      groups[catName].transactions.push(tx);
    });
    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [periodTransactions, transactionTags]);

  const totalExpensesFromTx = expensesByCategory.reduce((s, g) => s + g.total, 0);

  // Ingresos: transactions tagged with categories of type 'Ingreso'
  const incomesByCategory = React.useMemo(() => {
    const groups = {};
    periodTransactions.forEach(tx => {
      const catName = getTxDisplayCategory(tx);
      if (!catName) return;
      const catObj = getCategoryObj(catName);
      if (!catObj || catObj.type !== 'Ingreso') return;
      if (!groups[catName]) groups[catName] = { category: catName, total: 0, count: 0, transactions: [] };
      groups[catName].total += Math.abs(tx.amount);
      groups[catName].count += 1;
      groups[catName].transactions.push(tx);
    });
    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [periodTransactions, transactionTags]);

  const totalIncomeFromTx = incomesByCategory.reduce((s, g) => s + g.total, 0);

  // Quipu: facturado en los meses del período (los datos de Quipu cubren los últimos 12 meses)
  const quipuPeriodIncome = React.useMemo(() => {
    const byMonth = quipuData?.income_by_month || {};
    const fromM = periodFrom.slice(0, 7), toM = periodTo.slice(0, 7);
    const months = Object.keys(byMonth).filter((m) => m >= fromM && m <= toM);
    return { total: months.reduce((s, m) => s + (byMonth[m] || 0), 0), months: months.length };
  }, [quipuData, periodFrom, periodTo]);

  // Investments totals
  const totalCuotasMensuales = investments.filter(i => i.cuotaMensual > 0).reduce((sum, i) => sum + (i.cuotaMensual || 0), 0);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  // Export CSV
  const exportTransactionsCSV = () => {
    const headers = ['Fecha', 'Descripcion', 'Categoria', 'Importe'];
    const rows = filteredAndSortedTransactions.map(tx => [
      tx.date_iso || tx.date || '',
      `"${(tx.description || '').replace(/"/g, '""')}"`,
      getTxDisplayCategory(tx) || 'Sin categoria',
      tx.amount?.toFixed(2) || '0.00'
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transacciones_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const hasActiveFilters = txSortOrder !== 'fecha-desc' || txDateFrom || txDateTo || txAmountMin !== '' || txAmountMax !== '' || txSearchQuery || txCategoryFilter;

  const fmt = (n) => (n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6" data-testid="finance-page">
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Finanzas</h1>
          <p className="text-muted-foreground mt-1">Control financiero completo de la agencia</p>
        </div>
        <div className="flex items-center gap-3">
          {isSyncing && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          {lastBankUpdate && (
            <span className="text-xs text-muted-foreground">
              Banco: {new Date(lastBankUpdate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button variant="outline" onClick={handleSync} disabled={isSyncing} data-testid="sync-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            Sincronizar
          </Button>
        </div>
      </motion.div>

      {/* Aviso de banco sin sincronizar (PSD2) */}
      <BankSyncAlert />

      {/* Selector de período (aplica a Ingresos, Gastos y Flujo Neto a la vez) */}
      <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border bg-muted/30">
        <FinancePeriodSelector period={finPeriod} onChange={setFinPeriod} years={periodYears} monthsWithData={monthsWithData} coverage={periodCoverage} testPrefix="fin-period" />
        <p className="text-xs text-muted-foreground" data-testid="fin-period-count">
          {periodTransactions.length} movimientos del banco en el período · fuente: Caja Rural vía Fintable
        </p>
      </motion.div>
      {!periodHasBankData && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800" data-testid="fin-period-no-bank">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Sin movimientos bancarios en <strong>{periodLabel}</strong>.
            {periodBeforeBank
              ? ` El histórico del banco (Fintable) empieza el ${fmtDateEs(bankFirstDate)}; para ver la facturación de ese período consulta la pestaña Ingresos (Quipu).`
              : ' Comprueba la sincronización de Fintable o elige otro período.'}
          </span>
        </div>
      )}

      {/* KPIs */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover bg-gradient-to-br from-emerald-50 to-emerald-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Saldo Real · hoy</p>
                <p className="text-2xl font-bold text-emerald-700" data-testid="kpi-balance">{fmt(realTotalBalance)}</p>
                <p className="text-xs text-emerald-600">Caja Rural Navarra</p>
              </div>
              <Wallet className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Ingresos Totales · banco</p>
                <p className="text-2xl font-bold text-blue-700" data-testid="kpi-income">{periodHasBankData ? fmt(realTotalIncome) : '—'}</p>
                <p className="text-xs text-blue-600" data-testid="kpi-income-period">{periodLabel}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Gastos Totales · banco</p>
                <p className="text-2xl font-bold text-red-700" data-testid="kpi-expenses">{periodHasBankData ? fmt(realTotalExpenses) : '—'}</p>
                <p className="text-xs text-red-600" data-testid="kpi-expenses-period">{periodLabel}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Flujo Neto · banco</p>
                <p className={`text-2xl font-bold ${(realTotalIncome - realTotalExpenses) >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="kpi-net">
                  {periodHasBankData ? fmt(realTotalIncome - realTotalExpenses) : '—'}
                </p>
                <p className="text-xs text-purple-600" data-testid="kpi-net-period">{periodLabel}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={itemVariants}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full ${isCEO ? 'grid-cols-9' : 'grid-cols-7'}`}>
            <TabsTrigger value="banco-real" data-testid="tab-banco-real">Banco Real</TabsTrigger>
            <TabsTrigger value="quipu" data-testid="tab-quipu">Quipu</TabsTrigger>
            <TabsTrigger value="transacciones" data-testid="tab-transacciones">Transacciones</TabsTrigger>
            <TabsTrigger value="conciliacion" data-testid="tab-conciliacion">Conciliacion</TabsTrigger>
            <TabsTrigger value="gastos" data-testid="tab-gastos">Gastos</TabsTrigger>
            <TabsTrigger value="ingresos" data-testid="tab-ingresos">Ingresos</TabsTrigger>
            {isCEO && <TabsTrigger value="rentabilidad" data-testid="tab-rentabilidad">Rentabilidad</TabsTrigger>}
            {isCEO && <TabsTrigger value="incidencias-cobro" data-testid="tab-incidencias-cobro">Incidencias Cobro</TabsTrigger>}
          </TabsList>

          {/* ==================== BANCO REAL ==================== */}
          <TabsContent value="banco-real" className="space-y-6 mt-6">
            {isCEO && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BankConsentCard />
                <FinanceWebhookCard />
              </div>
            )}
            {bankError && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-700">{bankError}</p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="w-5 h-5 text-primary" />
                      Cuentas Bancarias
                    </CardTitle>
                    <CardDescription>Datos sincronizados desde Google Sheets</CardDescription>
                  </div>
                  <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Sincronizado
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {bankLoading && realBankAccounts.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : realBankAccounts.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay cuentas bancarias</p>
                ) : (
                  <div className="space-y-4">
                    {realBankAccounts.map((account, i) => (
                      <div key={i} className="p-4 rounded-lg border hover:shadow-md transition-all bg-gradient-to-r from-emerald-50 to-white">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 rounded-full bg-emerald-100">
                              <Building className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{account.name}</h3>
                              <p className="text-sm text-muted-foreground">{account.institution}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold text-emerald-600">{fmt(account.balance)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-emerald-600">{fmt(realTotalIncome)}</p>
                  <p className="text-sm text-muted-foreground">Total Ingresos · {periodLabel}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <TrendingDown className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-600">{fmt(realTotalExpenses)}</p>
                  <p className="text-sm text-muted-foreground">Total Gastos · {periodLabel}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Calculator className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className={`text-2xl font-bold ${(realTotalIncome - realTotalExpenses) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {fmt(realTotalIncome - realTotalExpenses)}
                  </p>
                  <p className="text-sm text-muted-foreground">Resultado Neto · {periodLabel}</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==================== QUIPU ==================== */}
          <TabsContent value="quipu" className="space-y-6 mt-6">
            {quipuLoading && !quipuData ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : quipuError && !quipuData ? (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-6 text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-700">{quipuError}</p>
                  <Button variant="outline" onClick={loadQuipuDataBackground} className="mt-4">Reintentar</Button>
                </CardContent>
              </Card>
            ) : !quipuConnected && !quipuData ? (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-6 text-center">
                  <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <p className="text-amber-700">Quipu no esta conectado</p>
                  <p className="text-sm text-muted-foreground mt-2">Configura las credenciales de Quipu para ver tus datos de facturacion</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Header + Month Selector */}
                <div className="flex items-center justify-between" data-testid="quipu-header">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Conectado a Quipu
                    </Badge>
                    <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50" data-testid="quipu-income-only-badge">
                      Solo facturación de ingresos · los gastos salen del banco
                    </Badge>
                    {quipuLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  </div>
                  <div className="flex items-center gap-3">
                    {/* MONTH SELECTOR */}
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <select
                        value={selectedQuipuMonth}
                        onChange={(e) => setSelectedQuipuMonth(e.target.value)}
                        className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        data-testid="quipu-month-selector"
                      >
                        {availableQuipuMonths.map(m => (
                          <option key={m.key} value={m.key}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        setQuipuLoading(true);
                        try {
                          await quipuAPI.clearCache();
                          await loadQuipuDataBackground();
                        } catch (error) {
                          console.error('Error refreshing Quipu data:', error);
                        }
                      }}
                      disabled={quipuLoading}
                      data-testid="quipu-refresh-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${quipuLoading ? 'animate-spin' : ''}`} />
                      Actualizar
                    </Button>
                  </div>
                </div>

                {/* KPIs for selected month */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Facturado en Quipu</p>
                          <p className="text-2xl font-bold" data-testid="quipu-month-income">{fmt(quipuMonthData.income)}</p>
                          <p className="text-xs text-muted-foreground mt-1">{MONTH_NAMES[parseInt(selectedQuipuMonth.split('-')[1]) - 1]} · facturas emitidas</p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/80">
                          <Receipt className="w-6 h-6 text-emerald-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-red-50 to-red-100/50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Gastos del mes · banco</p>
                          <p className="text-2xl font-bold text-red-700" data-testid="quipu-month-expenses">{quipuMonthData.bankHasData ? fmt(quipuMonthData.expenses) : '—'}</p>
                          <p className="text-xs text-muted-foreground mt-1">{quipuMonthData.bankHasData ? 'Salidas reales en Caja Rural (Fintable)' : 'Sin movimientos bancarios este mes'}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/80">
                          <TrendingDown className="w-6 h-6 text-red-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Facturado este año</p>
                          <p className="text-2xl font-bold">{fmt(quipuData?.year_to_date?.income)}</p>
                          <p className="text-xs text-emerald-600 mt-1">Neto (vs gastos banco): {fmt((quipuData?.year_to_date?.income || 0) - bankYtd.expenses)}</p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/80">
                          <TrendingUp className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={quipuData?.overdue?.count > 0 ? "border-red-200" : ""}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Pendientes Cobro</p>
                          <p className="text-2xl font-bold">{fmt(quipuData?.pending?.total)}</p>
                          <p className="text-xs text-muted-foreground mt-1">{quipuData?.pending?.count || 0} facturas</p>
                        </div>
                        <div className="p-3 rounded-xl bg-white/80">
                          <Clock className="w-6 h-6 text-amber-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Monthly Chart */}
                {quipuData?.monthly_chart?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-primary" />
                        Facturado en Quipu vs Gastos del banco
                      </CardTitle>
                      <CardDescription>Ingresos = facturas emitidas en Quipu · Gastos = salidas reales en Caja Rural (Fintable)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={quipuChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v) => `${v.toLocaleString('es-ES', {minimumFractionDigits: 2})}`} />
                          <Legend />
                          <Bar dataKey="income" name="Facturado (Quipu)" fill="#10b981" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="expenses" name="Gastos (banco)" fill="#f56258" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Resumen del Mes seleccionado + Acumulado Anual */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Resumen de {MONTH_NAMES[parseInt(selectedQuipuMonth.split('-')[1]) - 1]}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-50">
                          <span className="text-sm font-medium">Facturado (Quipu)</span>
                          <span className="text-lg font-bold text-emerald-600">{fmt(quipuMonthData.income)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-red-50">
                          <span className="text-sm font-medium">Gastos (banco)</span>
                          <span className="text-lg font-bold text-red-600">{quipuMonthData.bankHasData ? fmt(quipuMonthData.expenses) : '—'}</span>
                        </div>
                        {/* MANUAL PAYROLL INPUT */}
                        <div className="p-3 rounded-lg bg-blue-50 space-y-2" data-testid="payroll-section">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Nominas (manual)</span>
                            <span className="text-lg font-bold text-blue-600">{fmt(payrollData[selectedQuipuMonth] || 0)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              placeholder="Importe nominas..."
                              value={payrollInput}
                              onChange={(e) => setPayrollInput(e.target.value)}
                              className="h-8 text-sm"
                              data-testid="payroll-input"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleSavePayroll}
                              disabled={payrollSaving}
                              className="h-8 px-3"
                              data-testid="payroll-save-btn"
                            >
                              {payrollSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                        <div className="border-t pt-4 flex justify-between items-center">
                          <span className="font-semibold">Resultado Neto</span>
                          <span className={`text-xl font-bold ${quipuMonthData.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="quipu-month-net">
                            {fmt(quipuMonthData.net)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-blue-600" />
                        Acumulado Anual
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-50">
                          <span className="text-sm font-medium">Facturado (Quipu)</span>
                          <span className="text-lg font-bold text-emerald-600">{fmt(quipuData?.year_to_date?.income)}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg bg-red-50">
                          <span className="text-sm font-medium">Gastos (banco)</span>
                          <span className="text-lg font-bold text-red-600" data-testid="quipu-ytd-bank-expenses">{fmt(bankYtd.expenses)}</span>
                        </div>
                        <div className="border-t pt-4 flex justify-between items-center">
                          <span className="font-semibold">Beneficio Anual</span>
                          <span className={`text-xl font-bold ${((quipuData?.year_to_date?.income || 0) - bankYtd.expenses) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="quipu-ytd-net">
                            {fmt((quipuData?.year_to_date?.income || 0) - bankYtd.expenses)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* ==================== TRANSACCIONES ==================== */}
          <TabsContent value="transacciones" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-primary" />
                      Transacciones
                    </CardTitle>
                    <CardDescription>
                      {filteredAndSortedTransactions.length} de {realTransactions.length} movimientos
                      {hasActiveFilters && ' (filtrado)'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasActiveFilters && (
                      <Button variant="ghost" size="sm" onClick={clearTxFilters}>
                        <X className="w-4 h-4 mr-1" />Limpiar filtros
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={exportTransactionsCSV} data-testid="export-csv-btn">
                      <Download className="w-4 h-4 mr-2" />Exportar CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filters */}
                <div className="p-4 rounded-lg border bg-muted/30 space-y-4" data-testid="transaction-filters">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Filter className="w-4 h-4" />
                    Filtros
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Ordenar por</label>
                      <select
                        value={txSortOrder}
                        onChange={(e) => setTxSortOrder(e.target.value)}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        data-testid="tx-sort-order"
                      >
                        <option value="fecha-desc">Mas nueva primero</option>
                        <option value="fecha-asc">Mas antigua primero</option>
                        <option value="importe-desc">Mayor importe</option>
                        <option value="importe-asc">Menor importe</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Desde</label>
                      <Input type="date" value={txDateFrom} onChange={(e) => setTxDateFrom(e.target.value)} className="h-9" data-testid="tx-date-from" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Hasta</label>
                      <Input type="date" value={txDateTo} onChange={(e) => setTxDateTo(e.target.value)} className="h-9" data-testid="tx-date-to" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Importe min.</label>
                        <Input type="number" placeholder="0" value={txAmountMin} onChange={(e) => setTxAmountMin(e.target.value)} className="h-9" data-testid="tx-amount-min" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Importe max.</label>
                        <Input type="number" placeholder="∞" value={txAmountMax} onChange={(e) => setTxAmountMax(e.target.value)} className="h-9" data-testid="tx-amount-max" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Categoria</label>
                      <select
                        value={txCategoryFilter}
                        onChange={(e) => setTxCategoryFilter(e.target.value)}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        data-testid="tx-category-filter"
                      >
                        <option value="">Todas</option>
                        <option value="__uncategorized__">Sin categoria</option>
                        {allCategories.map(c => (
                          <option key={c.name} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar por nombre o concepto..."
                        value={txSearchInput}
                        onChange={(e) => setTxSearchInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                        className="pl-9 h-9"
                        data-testid="tx-search-input"
                      />
                    </div>
                    <Button size="sm" onClick={applySearch} data-testid="tx-search-btn">
                      <Search className="w-4 h-4 mr-1" />Buscar
                    </Button>
                  </div>
                </div>

                {/* Transaction list */}
                {bankLoading && realTransactions.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : filteredAndSortedTransactions.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">
                      {realTransactions.length === 0 ? 'No hay transacciones' : 'No hay transacciones que coincidan con los filtros'}
                    </p>
                    {hasActiveFilters && <Button variant="link" onClick={clearTxFilters} className="mt-2">Limpiar filtros</Button>}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {filteredAndSortedTransactions.map((tx, i) => {
                      const txKey = getTxKey(tx);
                      const catName = getTxDisplayCategory(tx);
                      const catObj = catName ? getCategoryObj(catName) : null;

                      return (
                        <div
                          key={i}
                          className={`p-3 rounded-lg border flex items-center justify-between cursor-pointer hover:shadow-md transition-all ${tx.amount > 0 ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-400'}`}
                          onClick={() => setSelectedTxForModal(tx)}
                          data-testid={`tx-row-${i}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`p-2 rounded-full shrink-0 ${tx.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                              {tx.amount > 0 ? <ArrowUpRight className="w-4 h-4 text-emerald-600" /> : <ArrowDownRight className="w-4 h-4 text-red-600" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{tx.description || 'Sin descripcion'}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                <span>{formatDate(tx.date_iso || tx.date)}</span>
                                {catObj ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                    style={{ backgroundColor: catObj.bg, color: catObj.text }}
                                    data-testid={`tx-category-badge-${i}`}
                                  >
                                    {catName}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-muted-foreground bg-gray-100" data-testid={`tx-category-badge-${i}`}>
                                    <Tag className="w-3 h-3" />
                                    Sin categoria
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <p className={`font-bold shrink-0 ml-3 ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {tx.amount > 0 ? '+' : ''}{fmt(tx.amount)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== CONCILIACION ==================== */}
          <TabsContent value="conciliacion" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                      Conciliacion Bancaria
                    </CardTitle>
                    <CardDescription>Compara los datos del banco con tu contabilidad (Quipu) para el mismo periodo</CardDescription>
                  </div>
                </div>
                {/* Period Selector */}
                <div className="flex items-center gap-4 mt-4 p-3 rounded-lg border bg-muted/30" data-testid="conc-period-selector">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Periodo:</span>
                  <Input
                    type="date"
                    value={concPeriodFrom}
                    onChange={(e) => setConcPeriodFrom(e.target.value)}
                    className="h-8 w-auto"
                    data-testid="conc-date-from"
                  />
                  <span className="text-sm text-muted-foreground">a</span>
                  <Input
                    type="date"
                    value={concPeriodTo}
                    onChange={(e) => setConcPeriodTo(e.target.value)}
                    className="h-8 w-auto"
                    data-testid="conc-date-to"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-8">
                  {/* Bank Column */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg border-b pb-2 flex items-center gap-2">
                      <Building className="w-5 h-5" /> Segun el Banco
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between p-3 rounded-lg bg-blue-50">
                        <span>Ingresos periodo</span>
                        <span className="font-bold text-blue-600">{fmt(concBankData.income)}</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-red-50">
                        <span>Gastos periodo</span>
                        <span className="font-bold text-red-600">{fmt(concBankData.expenses)}</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-emerald-50">
                        <span>Neto periodo</span>
                        <span className={`font-bold ${concBankData.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(concBankData.net)}</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-gray-100">
                        <span>Transacciones</span>
                        <span className="font-bold">{concBankData.count}</span>
                      </div>
                    </div>
                  </div>

                  {/* Quipu Column */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg border-b pb-2 flex items-center gap-2">
                      <Receipt className="w-5 h-5" /> Segun Contabilidad (Quipu)
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between p-3 rounded-lg bg-blue-50">
                        <span>Ingresos periodo</span>
                        <span className="font-bold text-blue-600">{fmt(concQuipuData.income)}</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-red-50">
                        <span>Gastos periodo</span>
                        <span className="font-bold text-red-600">{fmt(concQuipuData.expenses)}</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-emerald-50">
                        <span>Neto periodo</span>
                        <span className={`font-bold ${concQuipuData.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(concQuipuData.net)}</span>
                      </div>
                      <div className="flex justify-between p-3 rounded-lg bg-gray-100">
                        <span>Fuente</span>
                        <span className="font-bold text-sm">{quipuConnected ? 'Quipu API' : 'No conectado'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Difference */}
                <div className="mt-6 p-4 rounded-lg border-2 border-dashed">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Diferencia Ingresos</h4>
                      <p className="text-sm text-muted-foreground">Banco vs Contabilidad (mismo periodo)</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${Math.abs(concBankData.income - concQuipuData.income) < 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {fmt(concBankData.income - concQuipuData.income)}
                      </p>
                      {Math.abs(concBankData.income - concQuipuData.income) < 100 ? (
                        <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Conciliado</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" />Revisar</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <div>
                      <h4 className="font-semibold">Diferencia Gastos</h4>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${Math.abs(concBankData.expenses - concQuipuData.expenses) < 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {fmt(concBankData.expenses - concQuipuData.expenses)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== GASTOS (from Transactions) ==================== */}
          <TabsContent value="gastos" className="space-y-6 mt-6">
            {isCEO && <ExpenseForecastSection refreshKey={forecastRefresh} />}
            {isCEO && <ExpenseInvoicesCard />}
            <div className="p-3 rounded-lg border bg-muted/30">
              <FinancePeriodSelector period={finPeriod} onChange={setFinPeriod} years={periodYears} monthsWithData={monthsWithData} coverage={periodCoverage} testPrefix="gastos-period" />
            </div>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-red-500" />
                      Gastos por Categoria
                    </CardTitle>
                    <CardDescription>
                      Transacciones bancarias categorizadas como gastos · {periodLabel}
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total Gastos · {periodLabel}</p>
                    <p className="text-xl font-bold text-red-600" data-testid="total-expenses-tx">{fmt(totalExpensesFromTx)}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {expensesByCategory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay gastos categorizados en el período ({periodLabel})</p>
                ) : (
                  <div className="space-y-4">
                    {/* Pie Chart */}
                    {expensesByCategory.length > 1 && (
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={expensesByCategory}
                              dataKey="total"
                              nameKey="category"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({ category, total }) => `${category}: ${fmt(total)}`}
                            >
                              {expensesByCategory.map((entry, index) => (
                                <Cell key={index} fill={getCategoryBg(entry.category)} stroke={getCategoryText(entry.category)} strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => fmt(v)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Category list */}
                    <div className="space-y-3">
                      {expensesByCategory.map((group, i) => (
                        <div key={i} className="p-4 rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getCategoryBg(group.category), border: `2px solid ${getCategoryText(group.category)}` }} />
                              <span className="font-semibold">{group.category}</span>
                              <Badge variant="outline" className="text-xs">{group.count} movimientos</Badge>
                            </div>
                            <span className="font-bold text-red-600">{fmt(group.total)}</span>
                          </div>
                          <Progress value={totalExpensesFromTx > 0 ? (group.total / totalExpensesFromTx) * 100 : 0} className="h-2" />
                          {/* Show top transactions in this category */}
                          <div className="mt-2 space-y-1">
                            {group.transactions.slice(0, 3).map((tx, j) => (
                              <div key={j} className="flex justify-between text-xs text-muted-foreground">
                                <span className="truncate mr-2">{tx.description}</span>
                                <span className="shrink-0">{fmt(Math.abs(tx.amount))}</span>
                              </div>
                            ))}
                            {group.transactions.length > 3 && (
                              <p className="text-xs text-muted-foreground">... y {group.transactions.length - 3} mas</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== INGRESOS (from Transactions) ==================== */}
          <TabsContent value="ingresos" className="space-y-6 mt-6">
            <div className="p-3 rounded-lg border bg-muted/30">
              <FinancePeriodSelector period={finPeriod} onChange={setFinPeriod} years={periodYears} monthsWithData={monthsWithData} coverage={periodCoverage} testPrefix="ingresos-period" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Ingresos en banco · {periodLabel}</p>
                  <p className="text-2xl font-bold text-emerald-600" data-testid="ingresos-bank-period">{periodHasBankData ? fmt(realTotalIncome) : '—'}</p>
                  <p className="text-xs text-muted-foreground">{periodHasBankData ? `Entradas reales en Caja Rural (Fintable) · ${periodTransactions.filter((tx) => tx.amount > 0).length} movimientos` : `Sin movimientos bancarios en este período (histórico del banco desde ${fmtDateEs(bankFirstDate)})`}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Facturado en Quipu · {periodLabel}</p>
                  {quipuConnected && quipuPeriodIncome.months > 0 ? (
                    <>
                      <p className="text-2xl font-bold text-blue-600" data-testid="ingresos-quipu-period">{fmt(quipuPeriodIncome.total)}</p>
                      <p className="text-xs text-muted-foreground">Facturas emitidas · {quipuPeriodIncome.months} {quipuPeriodIncome.months === 1 ? 'mes' : 'meses'} con datos</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-muted-foreground" data-testid="ingresos-quipu-period">—</p>
                      <p className="text-xs text-muted-foreground">{quipuConnected ? 'Sin datos de Quipu para este período (cubre los últimos 12 meses)' : 'Quipu no conectado'}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                      Ingresos por Categoria
                    </CardTitle>
                    <CardDescription>
                      Transacciones bancarias categorizadas como ingresos · {periodLabel}
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total Ingresos · {periodLabel}</p>
                    <p className="text-xl font-bold text-emerald-600" data-testid="total-income-tx">{fmt(totalIncomeFromTx)}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {incomesByCategory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay ingresos categorizados en el período ({periodLabel})</p>
                ) : (
                  <div className="space-y-4">
                    {/* Pie Chart */}
                    {incomesByCategory.length > 1 && (
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={incomesByCategory}
                              dataKey="total"
                              nameKey="category"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({ category, total }) => `${category}: ${fmt(total)}`}
                            >
                              {incomesByCategory.map((entry, index) => (
                                <Cell key={index} fill={getCategoryBg(entry.category)} stroke={getCategoryText(entry.category)} strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => fmt(v)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Category list */}
                    <div className="space-y-3">
                      {incomesByCategory.map((group, i) => (
                        <div key={i} className="p-4 rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getCategoryBg(group.category), border: `2px solid ${getCategoryText(group.category)}` }} />
                              <span className="font-semibold">{group.category}</span>
                              <Badge variant="outline" className="text-xs">{group.count} movimientos</Badge>
                            </div>
                            <span className="font-bold text-emerald-600">{fmt(group.total)}</span>
                          </div>
                          <Progress value={totalIncomeFromTx > 0 ? (group.total / totalIncomeFromTx) * 100 : 0} className="h-2" />
                          <div className="mt-2 space-y-1">
                            {group.transactions.slice(0, 3).map((tx, j) => (
                              <div key={j} className="flex justify-between text-xs text-muted-foreground">
                                <span className="truncate mr-2">{tx.description}</span>
                                <span className="shrink-0">{fmt(tx.amount)}</span>
                              </div>
                            ))}
                            {group.transactions.length > 3 && (
                              <p className="text-xs text-muted-foreground">... y {group.transactions.length - 3} mas</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== INVERSIONES ==================== */}
          <TabsContent value="inversiones" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <PiggyBank className="w-5 h-5 text-primary" />
                    Inversiones y Deuda
                  </CardTitle>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total Cuotas Mensuales</p>
                    <p className="text-xl font-bold text-primary">{fmt(totalCuotasMensuales)}/mes</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {investments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay inversiones registradas</p>
                ) : (
                  <div className="space-y-4">
                    {investments.map((inv, i) => (
                      <div key={i} className="p-4 rounded-lg border">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold">{inv.concepto}</h3>
                            <Badge variant="outline">{inv.tipo}</Badge>
                          </div>
                          {inv.pendiente > 0 && (
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Pendiente</p>
                              <p className="text-xl font-bold text-red-600">{fmt(inv.pendiente)}</p>
                            </div>
                          )}
                          {inv.invertido > 0 && (
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Invertido</p>
                              <p className="text-xl font-bold text-blue-600">{fmt(inv.invertido)}</p>
                            </div>
                          )}
                        </div>
                        {inv.cuotaMensual > 0 && (
                          <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-blue-700">Cuota Mensual</span>
                              <span className="font-bold text-blue-700">{fmt(inv.cuotaMensual)}/mes</span>
                            </div>
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                              <span>Cuotas pagadas: {inv.cuotasPagadas}/{inv.cuotasTotales}</span>
                              <span>{inv.cuotasTotales > 0 ? Math.round((inv.cuotasPagadas / inv.cuotasTotales) * 100) : 0}%</span>
                            </div>
                            <Progress value={inv.cuotasTotales > 0 ? (inv.cuotasPagadas / inv.cuotasTotales) * 100 : 0} className="mt-2 h-2" />
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                          {inv.retornoEsperado && <span>ROI: {inv.retornoEsperado}</span>}
                          <span>Plazo: {inv.plazo}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== RENTABILIDAD POR CLIENTE ==================== */}
          {isCEO && (
            <TabsContent value="rentabilidad" className="space-y-6 mt-6">
              <ProfitabilityTab />
            </TabsContent>
          )}

          {/* ==================== INCIDENCIAS DE COBRO ==================== */}
          {isCEO && (
            <TabsContent value="incidencias-cobro" className="space-y-6 mt-6">
              <BillingIncidentsSection />
            </TabsContent>
          )}
        </Tabs>
      </motion.div>

      {/* ==================== MODAL CATEGORIZAR TRANSACCION ==================== */}
      {selectedTxForModal && (() => {
        const tx = selectedTxForModal;
        const txKey = getTxKey(tx);
        const currentCat = getTxDisplayCategory(tx);
        const currentCatObj = currentCat ? getCategoryObj(currentCat) : null;

        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) { setSelectedTxForModal(null); setCatModalView('list'); } }}
            data-testid="tx-category-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col"
              data-testid="tx-category-modal"
            >
              {/* Modal Header */}
              <div className="p-5 border-b shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold">
                    {catModalView === 'new' ? 'Nueva Categoria' : catModalView === 'edit' ? 'Editar Categoria' : 'Categorizar Transaccion'}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedTxForModal(null); setCatModalView('list'); }} data-testid="tx-modal-close-btn">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                {catModalView === 'list' && (
                  <div className={`p-4 rounded-lg border ${tx.amount > 0 ? 'border-l-4 border-l-emerald-500 bg-emerald-50/50' : 'border-l-4 border-l-red-400 bg-red-50/50'}`}>
                    <p className="font-medium text-sm">{tx.description || 'Sin descripcion'}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground">{formatDate(tx.date_iso || tx.date)}</span>
                      <span className={`text-lg font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {tx.amount > 0 ? '+' : ''}{fmt(tx.amount)}
                      </span>
                    </div>
                    {currentCatObj && (
                      <div className="mt-2">
                        <span className="text-xs text-muted-foreground mr-2">Categoria actual:</span>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: currentCatObj.bg, color: currentCatObj.text }}
                        >
                          {currentCat}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Content area - scrollable */}
              <div className="p-5 overflow-y-auto flex-1">
                {catModalView === 'list' && tx.amount < 0 && (() => {
                  const rule = recurringRules.find((r) => r.source_tx_key === txKey);
                  const current = rule ? rule.periodicity : 'puntual';
                  const opts = [
                    { id: 'puntual', label: 'Puntual' },
                    { id: 'mensual', label: 'Mensual' },
                    { id: 'trimestral', label: 'Trimestral' },
                    { id: 'anual', label: 'Anual' },
                  ];
                  return (
                    <div className="mb-4 p-3 rounded-lg border bg-muted/30" data-testid="tx-recurrence-block">
                      <p className="text-sm font-medium text-muted-foreground mb-2">¿Es un gasto recurrente?</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        {opts.map((o) => (
                          <button
                            key={o.id}
                            className={`px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${current === o.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted border-border'}`}
                            onClick={() => handleSetRecurrence(tx, o.id)}
                            data-testid={`tx-recurrence-${o.id}`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                      {rule && (
                        <p className="text-[11px] text-muted-foreground mt-2">
                          Previsto ~día {rule.charge_day} · {Number(rule.amount).toLocaleString('es-ES', { minimumFractionDigits: 2 })} € · edítalo en Gastos → Gastos recurrentes
                        </p>
                      )}
                    </div>
                  );
                })()}
                {catModalView === 'list' && (
                  <>
                    <p className="text-sm font-medium text-muted-foreground mb-3">Selecciona una categoria:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Default categories */}
                      {DEFAULT_CATEGORIES.map((cat) => {
                        const isSelected = currentCat === cat.name;
                        return (
                          <button
                            key={cat.name}
                            className={`flex items-center gap-2 p-3 rounded-lg text-left text-sm font-medium transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                            style={{ backgroundColor: cat.bg, color: cat.text }}
                            onClick={() => handleTagTransaction(txKey, cat.name)}
                            data-testid={`tx-modal-cat-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
                          >
                            <span className="flex-1">{cat.name}</span>
                            <span className="text-xs opacity-70">{cat.type}</span>
                          </button>
                        );
                      })}
                      {/* Custom categories with edit/delete */}
                      {customCategories.map((cat, idx) => {
                        const isSelected = currentCat === cat.name;
                        return (
                          <div key={`custom-${idx}`} className="relative group">
                            <button
                              className={`w-full flex items-center gap-2 p-3 rounded-lg text-left text-sm font-medium transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                              style={{ backgroundColor: cat.bg, color: cat.text }}
                              onClick={() => handleTagTransaction(txKey, cat.name)}
                              data-testid={`tx-modal-cat-custom-${idx}`}
                            >
                              <span className="flex-1">{cat.name}</span>
                              <span className="text-xs opacity-70">{cat.type}</span>
                            </button>
                            <div className="absolute top-1 right-1 hidden group-hover:flex gap-1">
                              <button
                                className="p-1 rounded bg-white/90 shadow-sm hover:bg-white"
                                onClick={(e) => { e.stopPropagation(); startEditCategory(idx); }}
                                data-testid={`tx-modal-edit-cat-${idx}`}
                              >
                                <Edit2 className="w-3 h-3 text-gray-600" />
                              </button>
                              <button
                                className="p-1 rounded bg-white/90 shadow-sm hover:bg-red-50"
                                onClick={(e) => { e.stopPropagation(); handleDeleteCategory(idx); }}
                                data-testid={`tx-modal-delete-cat-${idx}`}
                              >
                                <X className="w-3 h-3 text-red-500" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* New category button */}
                    <button
                      className="w-full mt-3 p-3 rounded-lg border-2 border-dashed border-gray-300 text-sm font-medium text-gray-500 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                      onClick={() => { setCatFormData({ name: '', bg: '#E6F1FB', type: 'Gasto' }); setCatModalView('new'); }}
                      data-testid="tx-modal-new-cat-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Nueva categoria
                    </button>

                    {/* Remove category */}
                    {currentCat && (
                      <Button
                        variant="outline"
                        className="w-full mt-3"
                        onClick={() => handleTagTransaction(txKey, '')}
                        data-testid="tx-modal-remove-cat-btn"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Quitar categoria
                      </Button>
                    )}
                  </>
                )}

                {/* New / Edit category form */}
                {(catModalView === 'new' || catModalView === 'edit') && (
                  <div className="space-y-4" data-testid="tx-modal-cat-form">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Nombre</label>
                      <Input
                        placeholder="Ej: Marketing, Seguros..."
                        value={catFormData.name}
                        onChange={(e) => setCatFormData(p => ({ ...p, name: e.target.value }))}
                        data-testid="cat-form-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={catFormData.bg}
                          onChange={(e) => setCatFormData(p => ({ ...p, bg: e.target.value }))}
                          className="w-10 h-10 rounded-lg border cursor-pointer"
                          data-testid="cat-form-color"
                        />
                        <div
                          className="flex-1 p-3 rounded-lg text-sm font-medium"
                          style={{ backgroundColor: catFormData.bg, color: getContrastText(catFormData.bg) }}
                        >
                          {catFormData.name || 'Vista previa'}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Tipo</label>
                      <div className="flex gap-2">
                        <button
                          className={`flex-1 p-3 rounded-lg text-sm font-medium border-2 transition-all ${catFormData.type === 'Gasto' ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500'}`}
                          onClick={() => setCatFormData(p => ({ ...p, type: 'Gasto' }))}
                          data-testid="cat-form-type-gasto"
                        >
                          Gasto
                        </button>
                        <button
                          className={`flex-1 p-3 rounded-lg text-sm font-medium border-2 transition-all ${catFormData.type === 'Ingreso' ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500'}`}
                          onClick={() => setCatFormData(p => ({ ...p, type: 'Ingreso' }))}
                          data-testid="cat-form-type-ingreso"
                        >
                          Ingreso
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => { setCatModalView('list'); setEditingCatIdx(null); }}
                        data-testid="cat-form-cancel"
                      >
                        Cancelar
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={catModalView === 'edit' ? handleUpdateCategory : handleSaveNewCategory}
                        disabled={!catFormData.name.trim()}
                        data-testid="cat-form-save"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {catModalView === 'edit' ? 'Actualizar' : 'Guardar'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        );
      })()}

    </motion.div>
  );
}
